'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { createImageUrlBuilder } from '@sanity/image-url';
import { client } from '@/sanity/client';
import AuthModal from './AuthModal';
import { useAuth } from './AuthProvider';
import { brandName } from '@/lib/structuredData';
const builder = createImageUrlBuilder(client);
function urlFor(source: any) {
  return builder.image(source);
}
interface FooterLink {
  label: string;
  url: string;
  openInNewTab?: boolean;
}
interface FooterColumn {
  title?: string;
  links?: FooterLink[];
}
interface SocialMedia {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
}
interface FooterSettings {
  portraitImage?: any;
  taglineImage?: any;
  brokerageLogo?: any;
}
interface FooterProps {
  logo?: any;
  logoAlt?: string;
  siteTitle?: string;
  description?: string;
  columns?: FooterColumn[];
  socialMedia?: SocialMedia;
  contactInfo?: {
    email?: string;
    phone?: string;
    address?: string;
  };
  copyrightText?: string;
  footer?: FooterSettings;
}

// Default image URLs (used as fallbacks)
const DEFAULT_PORTRAIT = 'https://drupal-storage.s3.amazonaws.com/klug/public/2024-11/chris-portrait.jpg';
const DEFAULT_TAGLINE = 'https://drupal-storage.s3.amazonaws.com/klug/public/2024-11/footer-tagline%402x_20220524164420_0.png';
const DEFAULT_BROKERAGE_LOGO = 'https://drupal-storage.s3.amazonaws.com/klug/public/2024-11/assir-logo%402x.png';

// Office address fallback. Used when contactInfo.address is empty in
// Sanity settings. Google's LocalBusiness validator (which classifies
// this footer's RealEstateAgent microdata) reports the `address`
// property as invalid when streetAddress is missing, so we always
// emit a complete PostalAddress.
const DEFAULT_OFFICE_STREET_ADDRESS = '415 East Hyman Avenue';
const OFFICE_LOCALITY = 'Aspen';
const OFFICE_REGION = 'CO';
const OFFICE_POSTAL_CODE = '81611';
export default function Footer(t0) {
  const $ = _c(133);
  const {
    logoAlt: t1,
    siteTitle: t2,
    description,
    columns: t3,
    socialMedia,
    contactInfo,
    footer
  } = t0;
  t1 === undefined ? "Logo" : t1;
  const siteTitle = t2 === undefined ? "Chris Klug Properties" : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const columns = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Date().getFullYear();
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const currentYear = t5;
  const [email, setEmail] = useState("");
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const {
    user
  } = useAuth();
  let t6;
  if ($[3] !== footer) {
    t6 = footer?.portraitImage ? urlFor(footer.portraitImage).width(400).url() : DEFAULT_PORTRAIT;
    $[3] = footer;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const portraitUrl = t6;
  let t7;
  if ($[5] !== footer) {
    t7 = footer?.taglineImage ? urlFor(footer.taglineImage).width(800).url() : DEFAULT_TAGLINE;
    $[5] = footer;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const taglineUrl = t7;
  let t8;
  if ($[7] !== footer) {
    t8 = footer?.brokerageLogo ? urlFor(footer.brokerageLogo).width(400).url() : DEFAULT_BROKERAGE_LOGO;
    $[7] = footer;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const brokerageLogoUrl = t8;
  let t9;
  if ($[9] !== email) {
    t9 = e => {
      e.preventDefault();
      console.log("Newsletter signup:", email);
      setEmail("");
    };
    $[9] = email;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleNewsletterSubmit = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [{
      label: "Home",
      url: "/"
    }, {
      label: "Featured Properties",
      url: "/real-estate-for-sale"
    }, {
      label: "About Klug Properties",
      url: "/about"
    }, {
      label: "Blog",
      url: "/about/blog"
    }, {
      label: "Living Aspen",
      url: "/media/living-aspen-magazine"
    }, {
      label: "Market Reports",
      url: "/aspen-snowmass-market-reports"
    }];
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const defaultLinks = t10;
  const footerLinks = columns.length > 0 ? columns[0]?.links || defaultLinks : defaultLinks;
  let t11;
  if ($[12] !== siteTitle) {
    t11 = <meta itemProp="name" content={siteTitle} />;
    $[12] = siteTitle;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <meta itemProp="priceRange" content="$$$$" />;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== description) {
    t13 = description && <meta itemProp="description" content={description} />;
    $[15] = description;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== contactInfo) {
    t14 = contactInfo?.phone && <meta itemProp="telephone" content={contactInfo.phone} />;
    $[17] = contactInfo;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== contactInfo) {
    t15 = contactInfo?.email && <meta itemProp="email" content={contactInfo.email} />;
    $[19] = contactInfo;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = contactInfo?.address || DEFAULT_OFFICE_STREET_ADDRESS;
  let t17;
  if ($[21] !== t16) {
    t17 = <meta itemProp="streetAddress" content={t16} />;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <meta itemProp="addressLocality" content={OFFICE_LOCALITY} />;
    t19 = <meta itemProp="addressRegion" content={OFFICE_REGION} />;
    t20 = <meta itemProp="postalCode" content={OFFICE_POSTAL_CODE} />;
    t21 = <meta itemProp="addressCountry" content="US" />;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
    $[26] = t21;
  } else {
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t17) {
    t22 = <div itemProp="address" itemScope={true} itemType="https://schema.org/PostalAddress">{t17}{t18}{t19}{t20}{t21}</div>;
    $[27] = t17;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== socialMedia) {
    t23 = socialMedia?.facebook && <link itemProp="sameAs" href={socialMedia.facebook} />;
    $[29] = socialMedia;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== socialMedia) {
    t24 = socialMedia?.instagram && <link itemProp="sameAs" href={socialMedia.instagram} />;
    $[31] = socialMedia;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== socialMedia) {
    t25 = socialMedia?.twitter && <link itemProp="sameAs" href={socialMedia.twitter} />;
    $[33] = socialMedia;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== socialMedia) {
    t26 = socialMedia?.linkedin && <link itemProp="sameAs" href={socialMedia.linkedin} />;
    $[35] = socialMedia;
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  let t27;
  if ($[37] !== socialMedia) {
    t27 = socialMedia?.youtube && <link itemProp="sameAs" href={socialMedia.youtube} />;
    $[37] = socialMedia;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== portraitUrl) {
    t28 = <div className="lg:max-w-none md:w-4/12 xl:w-2/12 relative w-full m-0 flex flex-col justify-center items-center"><Image src={portraitUrl} alt="Chris Klug" width={184} height={237} className="w-[165px] -mt-6 md:mt-0 md:absolute" itemProp="image" /></div>;
    $[39] = portraitUrl;
    $[40] = t28;
  } else {
    t28 = $[40];
  }
  let t29;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span className="sr-only">Homepage</span>;
    $[41] = t29;
  } else {
    t29 = $[41];
  }
  let t30;
  if ($[42] !== siteTitle || $[43] !== taglineUrl) {
    t30 = <Link href="/" className="w-full md:w-8/12 xl:w-4/12 lg:max-w-none pb-0 xl:pl-10" itemProp="url">{t29}<Image src={taglineUrl} alt={siteTitle} width={416} height={80} className="w-full" itemProp="logo" /></Link>;
    $[42] = siteTitle;
    $[43] = taglineUrl;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] !== socialMedia?.facebook) {
    t31 = (socialMedia?.facebook || true) && <a className="p-3 text-white" href={socialMedia?.facebook || "https://www.facebook.com/KlugProperties"} title="Chris Klug Properties on Facebook" target="_blank" rel="noopener noreferrer"><svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 320 512" height="1em" width="1em"><path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" /></svg></a>;
    $[45] = socialMedia?.facebook;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== socialMedia?.instagram) {
    t32 = (socialMedia?.instagram || true) && <a className="p-3 text-white" href={socialMedia?.instagram || "https://www.instagram.com/klugproperties/"} title="Chris Klug Properties on Instagram" target="_blank" rel="noopener noreferrer"><svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em"><path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" /></svg></a>;
    $[47] = socialMedia?.instagram;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== socialMedia?.twitter) {
    t33 = (socialMedia?.twitter || true) && <a className="p-3 text-white" href={socialMedia?.twitter || "https://twitter.com/klugproperties"} title="Chris Klug Properties on Twitter" target="_blank" rel="noopener noreferrer"><svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em"><path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z" /></svg></a>;
    $[49] = socialMedia?.twitter;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== socialMedia?.youtube) {
    t34 = (socialMedia?.youtube || true) && <a className="p-3 text-white" href={socialMedia?.youtube || "https://www.youtube.com/user/klugproperties"} title="Chris Klug Properties on YouTube" target="_blank" rel="noopener noreferrer"><svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 576 512" height="1em" width="1em"><path d="M549.655 124.083c-6.281-23.65-24.787-42.276-48.284-48.597C458.781 64 288 64 288 64S117.22 64 74.629 75.486c-23.497 6.322-42.003 24.947-48.284 48.597-11.412 42.867-11.412 132.305-11.412 132.305s0 89.438 11.412 132.305c6.281 23.65 24.787 41.5 48.284 47.821C117.22 448 288 448 288 448s170.78 0 213.371-11.486c23.497-6.321 42.003-24.171 48.284-47.821 11.412-42.867 11.412-132.305 11.412-132.305s0-89.438-11.412-132.305zm-317.51 213.508V175.185l142.739 81.205-142.739 81.201z" /></svg></a>;
    $[51] = socialMedia?.youtube;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] !== t31 || $[54] !== t32 || $[55] !== t33 || $[56] !== t34) {
    t35 = <nav className="w-full xl:w-3/12 lg:max-w-none flex flex-wrap justify-center pt-4 md:pt-24 xl:pt-0 pb-2 text-4xl" aria-label="Social media">{t31}{t32}{t33}{t34}</nav>;
    $[53] = t31;
    $[54] = t32;
    $[55] = t33;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== brokerageLogoUrl) {
    t36 = <div className="w-1/2 md:w-1/4 lg:w-auto xl:w-1/4 lg:max-w-none"><Image src={brokerageLogoUrl} alt="Aspen Snowmass Sotheby's International Realty" width={200} height={60} className="w-full max-w-[200px]" /></div>;
    $[58] = brokerageLogoUrl;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== t28 || $[61] !== t30 || $[62] !== t35 || $[63] !== t36) {
    t37 = <div className="flex flex-col w-full relative mt-14"><div className="relative bg-[#00254a] max-w-none w-full flex flex-col items-center p-0"><div className="flex flex-wrap xl:flex-nowrap w-full mx-0 px-6 pb-6 md:px-0 md:py-10 md:max-w-xl xl:max-w-screen-xl justify-center items-center">{t28}{t30}{t35}{t36}</div></div></div>;
    $[60] = t28;
    $[61] = t30;
    $[62] = t35;
    $[63] = t36;
    $[64] = t37;
  } else {
    t37 = $[64];
  }
  let t38;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <h3 className="text-3xl w-full border-b border-gray-300 mb-9 pb-3 font-light">Sign Up For Email Newsletter</h3>;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <legend className="hidden">Contact</legend>;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <label htmlFor="email" className="sr-only">Email Address</label>;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  let t41;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = e_0 => setEmail(e_0.target.value);
    $[68] = t41;
  } else {
    t41 = $[68];
  }
  let t42;
  if ($[69] !== email) {
    t42 = <li>{t40}<input type="email" name="email" id="email" placeholder="EMAIL ADDRESS" required={true} value={email} onChange={t41} className="bg-transparent border-0 border-b border-gray-400 placeholder-gray-500 leading-10 w-full rounded-none focus:border-[var(--color-gold)] focus:ring-0 outline-none" /></li>;
    $[69] = email;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <li className="text-center italic"><button type="submit" className="max-w-sm border border-gray-400 px-3 capitalize cursor-pointer leading-10 w-full rounded-none hover:border-[var(--color-gold)] hover:text-[var(--color-gold)] transition-colors">Submit</button></li>;
    $[71] = t43;
  } else {
    t43 = $[71];
  }
  let t44;
  if ($[72] !== t42) {
    t44 = <fieldset className="!p-0 border-0">{t39}<ul className="!my-0 list-none grid gap-4">{t42}{t43}</ul></fieldset>;
    $[72] = t42;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== handleNewsletterSubmit || $[75] !== t44) {
    t45 = <div className="w-full lg:w-1/3 max-w-md px-4 lg:pr-7 lg:pl-0">{t38}<form onSubmit={handleNewsletterSubmit} className="bg-no-repeat bg-cover">{t44}</form></div>;
    $[74] = handleNewsletterSubmit;
    $[75] = t44;
    $[76] = t45;
  } else {
    t45 = $[76];
  }
  let t46;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <h3 className="text-3xl w-full border-b border-gray-300 mb-9 pb-3 font-light">Links</h3>;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] !== footerLinks) {
    t47 = footerLinks.map(_temp);
    $[78] = footerLinks;
    $[79] = t47;
  } else {
    t47 = $[79];
  }
  let t48;
  if ($[80] !== t47) {
    t48 = <div className="w-full lg:w-1/3 max-w-md px-4 lg:px-7">{t46}<div className="text-sm mb-4 space-y-2">{t47}</div></div>;
    $[80] = t47;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  let t49;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <h3 className="text-3xl w-full border-b border-gray-300 mb-9 pb-3 font-light">Contact</h3>;
    $[82] = t49;
  } else {
    t49 = $[82];
  }
  let t50;
  if ($[83] !== siteTitle) {
    t50 = brandName(siteTitle);
    $[83] = siteTitle;
    $[84] = t50;
  } else {
    t50 = $[84];
  }
  let t51;
  if ($[85] !== t50) {
    t51 = <div className="font-medium">{t50}</div>;
    $[85] = t50;
    $[86] = t51;
  } else {
    t51 = $[86];
  }
  const t52 = contactInfo?.address || DEFAULT_OFFICE_STREET_ADDRESS;
  let t53;
  if ($[87] !== t52) {
    t53 = <div>{t52}</div>;
    $[87] = t52;
    $[88] = t53;
  } else {
    t53 = $[88];
  }
  let t54;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <div>{OFFICE_LOCALITY}, {OFFICE_REGION} {OFFICE_POSTAL_CODE}</div>;
    $[89] = t54;
  } else {
    t54 = $[89];
  }
  let t55;
  if ($[90] !== contactInfo) {
    t55 = contactInfo?.phone && <div><a href={`tel:${contactInfo.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-[var(--color-gold)] transition-colors">{contactInfo.phone}</a></div>;
    $[90] = contactInfo;
    $[91] = t55;
  } else {
    t55 = $[91];
  }
  let t56;
  if ($[92] !== contactInfo) {
    t56 = contactInfo?.email && <div><a href={`mailto:${contactInfo.email}`} className="hover:text-[var(--color-gold)] transition-colors">{contactInfo.email}</a></div>;
    $[92] = contactInfo;
    $[93] = t56;
  } else {
    t56 = $[93];
  }
  let t57;
  if ($[94] !== t51 || $[95] !== t53 || $[96] !== t55 || $[97] !== t56) {
    t57 = <address className="not-italic text-sm leading-relaxed mb-6">{t51}{t53}{t54}{t55}{t56}</address>;
    $[94] = t51;
    $[95] = t53;
    $[96] = t55;
    $[97] = t56;
    $[98] = t57;
  } else {
    t57 = $[98];
  }
  let t58;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <dd className="text-sm mb-3"><Link href="/privacy" className="hover:text-[var(--color-gold)] transition-colors">Privacy Policy</Link></dd>;
    $[99] = t58;
  } else {
    t58 = $[99];
  }
  let t59;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <dd className="text-sm mb-3"><Link href="/terms-of-service" className="hover:text-[var(--color-gold)] transition-colors">Terms Of Service</Link></dd>;
    $[100] = t59;
  } else {
    t59 = $[100];
  }
  let t60;
  if ($[101] !== user) {
    t60 = <div className="flex flex-row flex-wrap justify-between gap-x-4 lg:mr-14"><dl>{t58}{t59}<dd className="text-sm mb-3">{user ? <Link href="/account" className="hover:text-[var(--color-gold)] transition-colors">My Account</Link> : <button onClick={() => setAuthModalOpen(true)} className="hover:text-[var(--color-gold)] transition-colors text-left">Sign In</button>}</dd></dl></div>;
    $[101] = user;
    $[102] = t60;
  } else {
    t60 = $[102];
  }
  let t61;
  if ($[103] !== t57 || $[104] !== t60) {
    t61 = <div className="w-full lg:w-1/3 max-w-md px-4 lg:pl-7 lg:pr-0">{t49}{t57}{t60}</div>;
    $[103] = t57;
    $[104] = t60;
    $[105] = t61;
  } else {
    t61 = $[105];
  }
  let t62;
  if ($[106] !== t45 || $[107] !== t48 || $[108] !== t61) {
    t62 = <div className="flex flex-row flex-wrap justify-center w-full gap-y-6 py-14 px-4">{t45}{t48}{t61}</div>;
    $[106] = t45;
    $[107] = t48;
    $[108] = t61;
    $[109] = t62;
  } else {
    t62 = $[109];
  }
  let t63;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <h3 className="text-3xl w-full border-b border-gray-300 mb-4 pb-3 font-light">Property Types</h3>;
    $[110] = t63;
  } else {
    t63 = $[110];
  }
  let t64;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <div className="w-full max-w-screen-xl px-4 pb-12">{t63}<div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-2 text-sm"><Link href="/real-estate-for-sale" className="hover:text-[var(--color-gold)] transition-colors">Homes for Sale</Link><Link href="/condos" className="hover:text-[var(--color-gold)] transition-colors">Condos</Link><Link href="/rentals" className="hover:text-[var(--color-gold)] transition-colors">Rentals</Link><Link href="/commercial" className="hover:text-[var(--color-gold)] transition-colors">Commercial</Link><Link href="/land" className="hover:text-[var(--color-gold)] transition-colors">Land</Link><Link href="/exclusive-and-new" className="hover:text-[var(--color-gold)] transition-colors">{"Exclusive & New"}</Link><Link href="/sold-by-klug-properties" className="hover:text-[var(--color-gold)] transition-colors">Sold by Klug Properties</Link><Link href="/off-market" className="hover:text-[var(--color-gold)] transition-colors">Off-Market Listings</Link><Link href="/open-houses" className="hover:text-[var(--color-gold)] transition-colors">Open Houses</Link></div></div>;
    $[111] = t64;
  } else {
    t64 = $[111];
  }
  let t65;
  if ($[112] !== t62) {
    t65 = <div className="flex flex-col items-center w-full max-w-screen-xl">{t62}{t64}</div>;
    $[112] = t62;
    $[113] = t65;
  } else {
    t65 = $[113];
  }
  let t66;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = <hr className="w-full max-w-[1440px] mx-auto mt-9 border-t border-gray-200" />;
    $[114] = t66;
  } else {
    t66 = $[114];
  }
  let t67;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <div className="w-full py-9 px-4"><div className="max-w-[1440px] w-full mx-auto text-[8px] text-gray-600"><p style={{
          maxWidth: "none",
          fontSize: "8px",
          marginBottom: "0.5em"
        }}>© {currentYear} Sotheby's International Realty Affiliates LLC. All rights reserved. Sotheby's International Realty® and the Sotheby's International Realty Logo are service marks licensed to Sotheby's International Realty Affiliates LLC and used with permission. Sotheby's International Realty Affiliates LLC fully supports the principles of the Fair Housing Act and the Equal Opportunity Act. Each office is independently owned and operated.</p><p style={{
          maxWidth: "none",
          fontSize: "8px",
          marginBottom: "6px"
        }}>This website is not the official website of Sotheby's International Realty. Real estate agents affiliated with Sotheby's International Realty are independent contractors and are not employees of Sotheby's International Realty.</p><p style={{
          maxWidth: "none",
          fontSize: "8px",
          marginBottom: "6px"
        }}>The information set forth on this site is based upon information which we consider reliable, but because it has been supplied by third parties to our franchisees (who in turn supplied it to us), we can not represent that it is accurate or complete, and it should not be relied upon as such. The offerings are subject to errors, omissions, changes, including price, or withdrawal without notice.</p><p style={{
          maxWidth: "none",
          fontSize: "8px",
          marginBottom: "0"
        }}>All dimensions are approximate and have not been verified by the selling party and can not be verified by Sotheby's International Realty Affiliates LLC. It is recommended that you hire a professional in the business of determining dimensions, such as an appraiser, architect or civil engineer, to determine such information.</p></div></div>;
    $[115] = t67;
  } else {
    t67 = $[115];
  }
  let t68;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <div className="text-xs text-center pb-7 text-gray-500">Real estate website design, development and optimization by{" "}<a href="https://www.organicreturn.com/" className="font-bold hover:text-[var(--color-gold)] transition-colors">Organic Return</a></div>;
    $[116] = t68;
  } else {
    t68 = $[116];
  }
  let t69;
  if ($[117] !== authModalOpen) {
    t69 = authModalOpen && <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center" onClick={() => setAuthModalOpen(false)} role="dialog" aria-modal="true"><div onClick={_temp2}><AuthModal onClose={() => setAuthModalOpen(false)} /></div><button onClick={() => setAuthModalOpen(false)} className="absolute top-6 right-6 text-white/80 hover:text-white" aria-label="Close"><svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" /></svg></button></div>;
    $[117] = authModalOpen;
    $[118] = t69;
  } else {
    t69 = $[118];
  }
  let t70;
  if ($[119] !== t11 || $[120] !== t13 || $[121] !== t14 || $[122] !== t15 || $[123] !== t22 || $[124] !== t23 || $[125] !== t24 || $[126] !== t25 || $[127] !== t26 || $[128] !== t27 || $[129] !== t37 || $[130] !== t65 || $[131] !== t69) {
    t70 = <footer className="relative flex flex-col items-center w-full" itemScope={true} itemType="https://schema.org/RealEstateAgent">{t11}{t12}{t13}{t14}{t15}{t22}{t23}{t24}{t25}{t26}{t27}{t37}{t65}{t66}{t67}{t68}{t69}</footer>;
    $[119] = t11;
    $[120] = t13;
    $[121] = t14;
    $[122] = t15;
    $[123] = t22;
    $[124] = t23;
    $[125] = t24;
    $[126] = t25;
    $[127] = t26;
    $[128] = t27;
    $[129] = t37;
    $[130] = t65;
    $[131] = t69;
    $[132] = t70;
  } else {
    t70 = $[132];
  }
  return t70;
}
function _temp2(e_1) {
  return e_1.stopPropagation();
}
function _temp(link, index) {
  return <p key={index}><Link href={link.url} target={link.openInNewTab ? "_blank" : undefined} rel={link.openInNewTab ? "noopener noreferrer" : undefined} className="hover:text-[var(--color-gold)] transition-colors">{link.label}</Link></p>;
}